<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <!-- Animated Elements -->
    <div class="bg-element element-1"></div>
    <div class="bg-element element-2"></div>
    <div class="bg-element element-3"></div>
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-blue-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-indigo-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-blue-300">
            <i class="bx bx-network-chart mr-2"></i>Enterprise Networking
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Virtual Private <span class="text-blue-400">Cloud</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          Create isolated, secure cloud environments with complete control over your network topology. Deploy scalable infrastructure with enterprise-grade security, custom routing, and seamless connectivity options.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-blue-500 text-blue-300 hover:bg-blue-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-network-chart mr-2"></i>
            View Architecture
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="bg-blue-500/10 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
            <div class="text-3xl font-bold text-blue-400 mb-1">100%</div>
            <div class="text-sm text-gray-400">Network Isolation</div>
          </div>
          <div class="bg-blue-500/10 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
            <div class="text-3xl font-bold text-blue-400 mb-1">Custom</div>
            <div class="text-sm text-gray-400">IP Addressing</div>
          </div>
          <div class="bg-blue-500/10 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
            <div class="text-3xl font-bold text-blue-400 mb-1">99.99%</div>
            <div class="text-sm text-gray-400">Uptime SLA</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/vpc-hero.png" alt="Virtual Private Cloud" class="w-full h-auto">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-network-chart text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Custom</div>
              <div class="text-xs">Subnets</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-indigo-600 to-blue-700 rounded-xl p-5 shadow-xl w-48">
          <div class="flex items-center">
            <i class="bx bx-shield-check text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Security</div>
              <div class="text-xs">Groups</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- VPC Components Section -->
<section class="py-20 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        VPC Components
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Virtual Private Cloud <span class="text-blue-600">Architecture</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Build secure, scalable network infrastructure with comprehensive VPC components designed for enterprise workloads and mission-critical applications.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Subnets & Networks -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-network-chart text-3xl text-white"></i>
          </div>
          <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full">Core</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Subnets & Networks</h3>
        <p class="text-gray-600 mb-4">
          Create public and private subnets with custom CIDR blocks, enabling logical separation of resources and multi-tier application architectures.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Custom CIDR blocks (10.0.0.0/8 to /16)</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Public & private subnets</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Multi-AZ deployment</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Auto-assign public IPs</span>
          </li>
        </ul>
      </div>

      <!-- Security Groups -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-red-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-shield-alt text-3xl text-white"></i>
          </div>
          <span class="bg-red-100 text-red-800 text-xs font-bold px-3 py-1 rounded-full">Security</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Security Groups</h3>
        <p class="text-gray-600 mb-4">
          Virtual firewalls that control inbound and outbound traffic at the instance level with stateful packet inspection and rule-based access control.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Stateful firewall rules</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Protocol-specific filtering</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Source/destination IP controls</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Port range specifications</span>
          </li>
        </ul>
      </div>

      <!-- Internet Gateway -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-green-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-globe text-3xl text-white"></i>
          </div>
          <span class="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full">Gateway</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Internet Gateway</h3>
        <p class="text-gray-600 mb-4">
          Horizontally scaled, redundant gateway that provides internet connectivity to public subnets with automatic failover and high availability.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Bidirectional internet access</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">High availability design</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">No bandwidth constraints</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Automatic scaling</span>
          </li>
        </ul>
      </div>

      <!-- NAT Gateway -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-amber-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-server text-3xl text-white"></i>
          </div>
          <span class="bg-amber-100 text-amber-800 text-xs font-bold px-3 py-1 rounded-full">NAT</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">NAT Gateway</h3>
        <p class="text-gray-600 mb-4">
          Enable outbound internet connectivity for private subnet resources while maintaining security isolation with managed NAT services.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Outbound-only internet access</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Managed service</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Automatic scaling</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">High availability</span>
          </li>
        </ul>
      </div>

      <!-- VPC Peering -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-purple-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-link text-3xl text-white"></i>
          </div>
          <span class="bg-purple-100 text-purple-800 text-xs font-bold px-3 py-1 rounded-full">Connect</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">VPC Peering</h3>
        <p class="text-gray-600 mb-4">
          Connect multiple VPCs privately with secure, high-bandwidth connections that enable resource sharing across isolated environments.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Private network connectivity</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Cross-region peering</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">No bandwidth limitations</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Encrypted connections</span>
          </li>
        </ul>
      </div>

      <!-- VPN Gateway -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-cyan-500 transform hover:-translate-y-2">
        <div class="flex justify-between items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
            <i class="bx bx-transfer text-3xl text-white"></i>
          </div>
          <span class="bg-cyan-100 text-cyan-800 text-xs font-bold px-3 py-1 rounded-full">VPN</span>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">VPN Gateway</h3>
        <p class="text-gray-600 mb-4">
          Secure site-to-site and client VPN connections with enterprise-grade encryption, enabling hybrid cloud architectures and remote access.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Site-to-site VPN</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">Client VPN access</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">IPsec encryption</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check text-blue-600 mr-2"></i>
            <span class="text-sm">BGP routing support</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- Advanced Features Section -->
<section class="py-20 bg-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-indigo-100 text-indigo-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Enterprise Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        VPC Advanced <span class="text-indigo-600">Capabilities</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Enterprise-grade networking features designed for complex infrastructures, regulatory compliance, and high-performance computing environments.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
      <!-- Network ACLs -->
      <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-alt text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Network ACLs</h3>
        <p class="text-gray-600 mb-4">
          Subnet-level security with stateless firewall rules providing an additional layer of protection for your VPC resources and network segments.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Stateless packet filtering</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Subnet-level protection</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Rule-based access control</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Custom rule priorities</span>
          </li>
        </ul>
      </div>

      <!-- VPC Flow Logs -->
      <div class="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-analyse text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">VPC Flow Logs</h3>
        <p class="text-gray-600 mb-4">
          Comprehensive network traffic monitoring and analysis with detailed flow logs for security auditing, troubleshooting, and compliance reporting.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Real-time traffic monitoring</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Security analysis</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Compliance logging</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Performance insights</span>
          </li>
        </ul>
      </div>

      <!-- DNS Resolution -->
      <div class="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-700 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-globe-alt text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">DNS Resolution</h3>
        <p class="text-gray-600 mb-4">
          Built-in DNS services with custom domain resolution, private hosted zones, and seamless integration with external DNS providers.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Private DNS zones</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Custom domain resolution</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>DNSSEC support</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-blue-600 mr-2"></i>
            <span>Health check integration</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- Technical Specs & Limits Section -->
<section class="py-20 bg-slate-900 text-white">
  <div class="container mx-auto px-4">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <div class="inline-block bg-blue-900/50 text-blue-300 border border-blue-500/30 px-4 py-1 rounded-full text-sm font-medium mb-4">
          Technical Specifications
        </div>
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          VPC <span class="text-blue-400">Limits & Quotas</span>
        </h2>
        <p class="text-gray-400 text-lg mb-8">
          Enterprise-scale limits designed to support massive workloads. Request quota increases instantly through the console.
        </p>
        
        <div class="overflow-hidden rounded-xl border border-gray-700 bg-gray-800/50">
          <table class="w-full text-left">
            <thead>
              <tr class="bg-gray-800 border-b border-gray-700">
                <th class="p-4 font-semibold text-gray-300">Resource</th>
                <th class="p-4 font-semibold text-gray-300">Default Limit</th>
                <th class="p-4 font-semibold text-gray-300">Scalability</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-700">
              <tr class="hover:bg-gray-700/50 transition-colors">
                <td class="p-4 text-blue-300 font-medium">
                  <i class="bx bx-network-chart mr-2 align-middle"></i>
                  <span class="align-middle">VPCs per Region</span>
                </td>
                <td class="p-4 text-gray-300">5</td>
                <td class="p-4 text-gray-400 text-sm">Soft Limit</td>
              </tr>
              <tr class="hover:bg-gray-700/50 transition-colors">
                <td class="p-4 text-blue-300 font-medium">
                  <i class="bx bx-grid-alt mr-2 align-middle"></i>
                  <span class="align-middle">Subnets per VPC</span>
                </td>
                <td class="p-4 text-gray-300">200</td>
                <td class="p-4 text-gray-400 text-sm">Hard Limit</td>
              </tr>
              <tr class="hover:bg-gray-700/50 transition-colors">
                <td class="p-4 text-blue-300 font-medium">
                  <i class="bx bx-merge mr-2 align-middle"></i>
                  <span class="align-middle">IPv4 CIDR Block</span>
                </td>
                <td class="p-4 text-gray-300">/16 to /28</td>
                <td class="p-4 text-gray-400 text-sm">Configurable</td>
              </tr>
              <tr class="hover:bg-gray-700/50 transition-colors">
                <td class="p-4 text-blue-300 font-medium">
                  <i class="bx bx-shield-alt mr-2 align-middle"></i>
                  <span class="align-middle">Security Groups</span>
                </td>
                <td class="p-4 text-gray-300">2,500</td>
                <td class="p-4 text-gray-400 text-sm">Per Region</td>
              </tr>
              <tr class="hover:bg-gray-700/50 transition-colors">
                <td class="p-4 text-blue-300 font-medium">
                  <i class="bx bx-git-branch mr-2 align-middle"></i>
                  <span class="align-middle">Route Tables</span>
                </td>
                <td class="p-4 text-gray-300">200</td>
                <td class="p-4 text-gray-400 text-sm">Per VPC</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="relative">
        <!-- Visual representation of VPC Topology -->
        <div class="bg-gray-800 rounded-2xl border border-gray-700 p-8 relative overflow-hidden">
          <div class="absolute top-0 right-0 p-4 opacity-10">
            <i class="bx bx-network-chart text-9xl text-blue-500"></i>
          </div>
          
          <h3 class="text-xl font-bold mb-6 flex items-center">
            <i class="bx bx-sitemap text-blue-400 mr-2"></i> VPC Topology
          </h3>
          
          <div class="space-y-4">
            <!-- Region Box -->
            <div class="border-2 border-dashed border-gray-600 rounded-xl p-4 relative">
              <div class="absolute -top-3 left-4 bg-gray-800 px-2 text-xs text-gray-500 font-mono">Region</div>
              
              <!-- VPC Box -->
              <div class="border-2 border-blue-500/50 rounded-xl p-4 bg-blue-900/10">
                <div class="flex justify-between items-center mb-4">
                  <span class="text-blue-400 font-bold text-sm">VPC (10.0.0.0/16)</span>
                  <span class="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded">Active</span>
                </div>
                
                <div class="grid grid-cols-2 gap-4">
                  <!-- Public Subnet -->
                  <div class="bg-gray-900/80 rounded-lg p-3 border border-gray-700">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-xs text-gray-400">Public Subnet</span>
                      <i class="bx bx-globe text-blue-400"></i>
                    </div>
                    <div class="h-2 bg-gray-700 rounded-full w-3/4 mb-1"></div>
                    <div class="h-2 bg-gray-700 rounded-full w-1/2"></div>
                  </div>
                  
                  <!-- Private Subnet -->
                  <div class="bg-gray-900/80 rounded-lg p-3 border border-gray-700">
                    <div class="flex items-center justify-between mb-2">
                      <span class="text-xs text-gray-400">Private Subnet</span>
                      <i class="bx bx-lock-alt text-red-400"></i>
                    </div>
                    <div class="h-2 bg-gray-700 rounded-full w-3/4 mb-1"></div>
                    <div class="h-2 bg-gray-700 rounded-full w-1/2"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="flex justify-between text-xs text-gray-500 px-2">
              <span>Internet Gateway</span>
              <span>Virtual Private Gateway</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Use Cases Section -->
<section class="py-20 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Application Scenarios
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        VPC <span class="text-blue-600">Use Cases</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Discover how Virtual Private Cloud enables secure, scalable architectures for enterprise applications, development environments, and hybrid cloud deployments.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      <!-- Multi-Tier Applications -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-blue-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-building text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">Enterprise</span>
            <h3 class="text-2xl font-bold text-gray-900">Multi-Tier Applications</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Deploy complex enterprise applications with web, application, and database tiers in separate subnets with controlled access and security isolation.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Web tier in public subnet</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>App tier in private subnet</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Database tier isolation</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Load balancer integration</span>
          </li>
        </ul>
      </div>

      <!-- Hybrid Connectivity -->
      <div class="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-indigo-500">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-network-chart text-3xl text-white"></i>
          </div>
          <div>
            <span class="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full mb-3 inline-block">Hybrid Cloud</span>
            <h3 class="text-2xl font-bold text-gray-900">Hybrid Connectivity</h3>
          </div>
        </div>
        <p class="text-gray-600 mb-6">
          Extend on-premises networks to the cloud with secure VPN connections, enabling hybrid architectures and gradual cloud migration strategies.
        </p>
        <ul class="space-y-3">
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Site-to-site VPN</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Dedicated connections</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Network extension</span>
          </li>
          <li class="flex items-center text-gray-700">
            <i class="bx bx-check-circle text-blue-600 mr-3 text-xl"></i>
            <span>Seamless integration</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-blue-600 via-indigo-700 to-blue-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Build Your Virtual Private Cloud?
      </h2>
      <p class="text-xl text-gray-100 mb-10">
        Create secure, isolated network environments with enterprise-grade features and complete control over your infrastructure. Start building your VPC today with our expert support team.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-blue-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-phone mr-2 text-xl"></i>
          <span class="text-lg">Contact Us</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-headphone mr-2 text-xl"></i>
          <span class="text-lg">Talk to Sales Team</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">99.99%</div>
          <div class="text-gray-200 text-sm">Network Uptime</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-gray-200 text-sm">Expert Support</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">100%</div>
          <div class="text-gray-200 text-sm">Network Isolation</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Free</div>
          <div class="text-gray-200 text-sm">VPC Creation</div>
        </div>
      </div>
    </div>
  </div>
</section>
